import React from 'react';
import classnames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons/faCheck';
import { useForm } from 'react-hook-form';

import AnonEmployerAssociation from 'buyside/components/presentation/EmployerAssociation/anonEmployerAssociation';
import AnonymousUserDisplay from 'buyside/components/presentation/AnonymousUserDisplay/anonymousUserDisplay';
import Button, { BUTTON_SIZES, BUTTON_TYPES } from 'buyside/components/presentation/Button/button';
import ProfileImage, {
  IMAGE_SIZES
} from 'buyside/components/presentation/ProfileImage/profileImage';
import Skeleton, { SKELETON_WIDTHS } from 'buyside/components/presentation/Skeleton/skeleton';

import {
  useAnonymousCurrentUserQuery,
  useSetUsernameMutation,
  SetUsernameMutationVariables
} from 'interfaces/graphql-typings';

import { defaultUserImageUrl } from 'buyside/util/urlHelper';

import styles from './usernameFormStyles.scss';

const UsernameForm: React.FC<{ userId?: string }> = ({ userId }) => {
  const { data, loading, error } = useAnonymousCurrentUserQuery();
  const [setUsername, { data: mutationData, error: mutationError }] = useSetUsernameMutation({
    update(cache, { data }) {
      if (!data?.setUsername?.currentUser?.username) return;

      const currentUser = data.setUsername.currentUser;
      cache.modify({
        id: cache.identify({ id: currentUser.id, __typename: 'User' }),
        fields: { username: () => currentUser.username }
      });
    }
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty, isSubmitting }
  } = useForm<SetUsernameMutationVariables>();

  return (
    <>
      {userId !== data?.currentUser?.id && (
        <p className={styles.error}>Admin: Become the user to update their username.</p>
      )}
      <p>
        Research posted anonymously does not include your name or profile photo. (Your identity is
        visible to SumZero Admins.)
      </p>
      <div className={styles.flex}>
        <div className={classnames(styles.flex, styles.profileContainer)}>
          <ProfileImage source={defaultUserImageUrl()} size={IMAGE_SIZES.SMALLER} />
          <div>
            {loading ? (
              <div>
                <Skeleton containerHeight={24} containerWidth={95} />
                <Skeleton containerHeight={17} containerWidth={95} />
                <Skeleton containerHeight={17} containerWidth={95} />
              </div>
            ) : (
              <>
                <div>
                  {data?.currentUser?.username ? (
                    <div>{data.currentUser.username}</div>
                  ) : (
                    <AnonymousUserDisplay
                      anonUser={{ title: data?.currentUser?.mostRecentEmployer?.title }}
                    />
                  )}
                </div>
                <AnonEmployerAssociation
                  aumRange={data?.currentUser?.mostRecentEmployer?.employer?.aumRange}
                  fundType={data?.currentUser?.mostRecentEmployer?.employer?.fundType}
                  profileCity={data?.currentUser?.profileCity}
                />
              </>
            )}
          </div>
        </div>
        <>
          {mutationData?.setUsername?.currentUser?.username && (
            <div className={styles.success}>
              Success
              <FontAwesomeIcon icon={faCheck} className={styles.icon} />
            </div>
          )}
        </>
      </div>
      {error && <p className={styles.error}>Something went wrong. Please refresh the page.</p>}
      <p className={styles.secondPar}>
        {loading ? (
          <Skeleton width={SKELETON_WIDTHS.LARGE} />
        ) : (
          <>
            {data?.currentUser?.username ? (
              <>
                Your current username is: <strong>{data.currentUser.username}</strong>
              </>
            ) : (
              <>
                You can optionally register a username that will appear on your anonymous reports.
              </>
            )}
          </>
        )}
      </p>
      <form
        onSubmit={(e): void => {
          e.stopPropagation();
          handleSubmit(formInput => setUsername({ variables: formInput }))(e);
        }}
      >
        <div className={styles.flex}>
          <div>
            <input
              id="username"
              placeholder={data?.currentUser?.username || 'Username'}
              type="text"
              {...register('username', {
                maxLength: { value: 30, message: 'Too long' }
              })}
            />
          </div>
          <Button
            className={styles.icon}
            disabled={!isDirty}
            isLoading={isSubmitting}
            onClick={(e): void => e.stopPropagation()}
            size={BUTTON_SIZES.TINY}
            text="Register"
            type={BUTTON_TYPES.MAJOR}
          />
        </div>
        {errors.username && <p className={styles.error}>{errors.username.message}</p>}
        {mutationError && <p className={styles.error}>{mutationError.message}</p>}
      </form>
    </>
  );
};

export default UsernameForm;
